import { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import html2pdf from "html2pdf.js";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Loader2, ChevronsUpDown, Check, FileText, Download, Pencil, Trash2, Eye, GraduationCap, Bot, AlertTriangle } from "lucide-react";

function StudentPicker({ students, value, onChange }) {
  const [open, setOpen] = useState(false);
  const sel = students.find((s) => s.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid="ai-student-picker" className="h-12 w-full justify-between rounded-xl px-4 font-normal">
          <span className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-violet-500" />
            {sel ? sel.full_name : <span className="text-slate-400">Öğrenci ara ve seç...</span>}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Öğrenci ara..." data-testid="ai-student-search" />
          <CommandList>
            <CommandEmpty>Öğrenci bulunamadı.</CommandEmpty>
            <CommandGroup>
              {students.map((s) => (
                <CommandItem key={s.id} value={s.full_name} onSelect={() => { onChange(s.id); setOpen(false); }} data-testid={`ai-student-opt-${s.id}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === s.id ? "opacity-100" : "opacity-0"}`} />
                  {s.full_name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function AiReports() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const printRef = useRef();

  useEffect(() => { api.get("/students").then((r) => setStudents(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setReports([]); setActive(null);
    if (!studentId) return;
    let cancelled = false;
    api.get(`/ai/reports?student_id=${studentId}`).then((r) => { if (!cancelled) setReports(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [studentId]);

  const askGenerate = () => {
    if (!studentId) { toast.error("Önce öğrenci seçin"); return; }
    setConfirmOpen(true);
  };

  const generate = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/reports", { student_id: studentId });
      setActive(data);
      setReports((p) => [data, ...p]);
      toast.success("Rapor oluşturuldu ✓");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setGenerating(false); }
  };

  const downloadPdf = (rep) => {
    const el = document.createElement("div");
    el.style.cssText = "padding:32px;font-family:Arial,sans-serif;color:#1e293b;line-height:1.6;width:720px;";
    el.innerHTML = `<h1 style="color:#7c3aed;font-size:22px;margin-bottom:4px;">${rep.title}</h1>
      <p style="color:#64748b;font-size:12px;margin-bottom:20px;">${rep.student_name} · ${(rep.created_at || "").slice(0, 10)}</p>
      <div style="white-space:pre-wrap;font-size:13px;">${(rep.content || "").replace(/</g, "&lt;")}</div>`;
    html2pdf().set({ margin: 10, filename: `${rep.title}.pdf`, html2canvas: { scale: 2 }, jsPDF: { unit: "mm", format: "a4" } }).from(el).save();
  };

  const saveEdit = async () => {
    try {
      const { data } = await api.put(`/ai/reports/${editing.id}`, { title: editing.title, content: editing.content });
      setReports((p) => p.map((r) => (r.id === data.id ? data : r)));
      if (active?.id === data.id) setActive(data);
      setEditing(null);
      toast.success("Kaydedildi");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/ai/reports/${id}`);
      setReports((p) => p.filter((r) => r.id !== id));
      if (active?.id === id) setActive(null);
      toast.success("Silindi");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="ai-reports-page">
        <header className="mb-5">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-violet-500"><Sparkles className="h-3.5 w-3.5" /> Yapay Zeka</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">AI Gelişim Raporu</h1>
        </header>

        <div className="mb-6 max-w-xl"><StudentPicker students={students} value={studentId} onChange={setStudentId} /></div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Sol: analiz / rapor */}
          <Card className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] lg:col-span-2" data-testid="ai-analysis-panel">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Bot className="h-5 w-5 text-violet-600" /> Analiz</h2>
              <Button onClick={askGenerate} disabled={generating || !studentId} data-testid="ai-generate-btn" className="rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:opacity-90">
                {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Oluşturuluyor…</> : <><Sparkles className="mr-2 h-4 w-4" /> Rapor Oluştur</>}
              </Button>
            </div>
            {generating ? (
              <div className="flex flex-col items-center gap-3 py-20 text-slate-400">
                <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
                <p className="text-sm">Öğrenci verileri analiz ediliyor…</p>
              </div>
            ) : active ? (
              <div ref={printRef} className="rounded-xl bg-slate-50/70 p-5" data-testid="ai-report-content">
                <h3 className="font-heading text-lg font-bold text-slate-900">{active.title}</h3>
                <p className="mb-3 text-xs text-slate-400">{active.student_name} · {(active.created_at || "").slice(0, 10)}</p>
                <div className="max-h-[52vh] overflow-y-auto whitespace-pre-wrap text-[15px] leading-relaxed text-slate-700">{active.content}</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-20 text-center text-slate-400">
                <Bot className="h-10 w-10 text-slate-300" />
                <p className="text-sm">{studentId ? "Rapor oluşturmak için “Rapor Oluştur”a bas veya arşivden bir rapor seç." : "Başlamak için bir öğrenci seç."}</p>
              </div>
            )}
          </Card>

          {/* Sağ: arşiv */}
          <Card className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="ai-archive-panel">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><FileText className="h-5 w-5 text-violet-600" /> Rapor Arşivi</h2>
            <div className="space-y-3">
              {reports.map((r) => (
                <div key={r.id} data-testid={`ai-report-${r.id}`} className={`rounded-xl border p-3 transition-colors ${active?.id === r.id ? "border-violet-300 bg-violet-50/50" : "border-slate-200"}`}>
                  <button onClick={() => setActive(r)} className="block w-full text-left">
                    <p className="font-heading text-sm font-semibold text-slate-900">{r.title}</p>
                    <p className="text-xs text-slate-400">{(r.created_at || "").slice(0, 10)}</p>
                  </button>
                  <div className="mt-2 flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setViewing(r)} data-testid={`ai-view-${r.id}`} className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100" title="Görüntüle"><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => downloadPdf(r)} data-testid={`ai-download-${r.id}`} className="h-8 w-8 rounded-lg text-blue-600 hover:bg-blue-50" title="PDF İndir"><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setEditing({ ...r })} data-testid={`ai-edit-${r.id}`} className="h-8 w-8 rounded-lg text-amber-600 hover:bg-amber-50" title="Düzenle"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r.id)} data-testid={`ai-delete-${r.id}`} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50" title="Sil"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
              {reports.length === 0 && <p className="py-10 text-center text-sm text-slate-400">{studentId ? "Bu öğrenci için henüz rapor yok." : "Öğrenci seçin."}</p>}
            </div>
          </Card>
        </div>
      </div>

      {/* Kredi onayı */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="ai-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-lg">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100"><AlertTriangle className="h-5 w-5 text-amber-600" /></span>
              Rapor Oluştur
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-slate-600" data-testid="ai-confirm-text">
            Bu işlem kredinizden düşecektir, onaylıyor musunuz?
          </p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="ai-confirm-cancel" className="rounded-xl">Vazgeç</Button>
            <Button onClick={generate} data-testid="ai-confirm-yes" className="rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:opacity-90">
              <Sparkles className="mr-2 h-4 w-4" /> Onayla ve Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Görüntüle */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl sm:max-w-2xl" data-testid="ai-view-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{viewing?.title}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">{viewing?.student_name} · {(viewing?.created_at || "").slice(0, 10)}</p>
          <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-slate-700">{viewing?.content}</div>
          <DialogFooter><Button onClick={() => downloadPdf(viewing)} className="rounded-xl bg-violet-600 text-white hover:bg-violet-700"><Download className="mr-2 h-4 w-4" /> PDF İndir</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Düzenle */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl sm:max-w-2xl" data-testid="ai-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Raporu Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Başlık</Label><Input value={editing?.title || ""} onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} data-testid="ai-edit-title" className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>İçerik</Label><Textarea value={editing?.content || ""} onChange={(e) => setEditing((p) => ({ ...p, content: e.target.value }))} data-testid="ai-edit-content" className="min-h-[300px] rounded-xl" /></div>
          </div>
          <DialogFooter><Button onClick={saveEdit} data-testid="ai-edit-save" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
